import React, { createContext, useContext, useState, useCallback } from 'react';
import { AnalysisTask, PromptOverrides } from '../types/analysis';
import { analysisService } from '../services/analysisService';
import { useAuth } from './AuthContext';

interface AnalysisContextType {
  tasks: AnalysisTask[];
  selectedTasks: string[];
  loading: boolean;
  error: Error | null;
  loadTasks: () => Promise<void>;
  toggleTask: (taskKey: string) => void;
  selectAllTasks: () => void;
  clearTasks: () => void;
  defaultPrompts: PromptOverrides | null;
  promptOverrides: PromptOverrides;
  loadDefaultPrompts: () => Promise<void>;
  setPromptOverride: (key: string, value: string) => void;
  setPromptOverrides: (overrides: PromptOverrides) => void;
  clearPromptOverrides: () => void;
}

const AnalysisContext = createContext<AnalysisContextType | undefined>(undefined);

export const useAnalysisContext = () => {
  const context = useContext(AnalysisContext);
  if (!context) {
    throw new Error('useAnalysisContext must be used within an AnalysisProvider');
  }
  return context;
};

interface AnalysisProviderProps {
  children: React.ReactNode;
}

export const AnalysisProvider: React.FC<AnalysisProviderProps> = ({ children }) => {
  const { token } = useAuth();
  const [tasks, setTasks] = useState<AnalysisTask[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [defaultPrompts, setDefaultPrompts] = useState<PromptOverrides | null>(null);
  const [promptOverrides, setPromptOverridesState] = useState<PromptOverrides>({});

  const loadTasks = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const availableTasks = await analysisService.getTasks(token);
      setTasks(availableTasks);
      // Select all tasks by default, except those requiring a style guide
      setSelectedTasks(availableTasks.filter(t => !t.requiresStyleGuide).map(t => t.taskId));
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const toggleTask = useCallback((taskKey: string) => {
    setSelectedTasks(prev => {
      if (prev.includes(taskKey)) {
        return prev.filter(k => k !== taskKey);
      } else {
        return [...prev, taskKey];
      }
    });
  }, []);

  const selectAllTasks = useCallback(() => {
    setSelectedTasks(prev => {
      const nonStyleGuideTasks = tasks.filter(t => !t.requiresStyleGuide).map(t => t.taskId);
      const styleGuideTasks = prev.filter(id => tasks.find(t => t.taskId === id)?.requiresStyleGuide);
      return [...nonStyleGuideTasks, ...styleGuideTasks];
    });
  }, [tasks]);

  const clearTasks = useCallback(() => {
    setSelectedTasks([]);
  }, []);

  const loadDefaultPrompts = useCallback(async () => {
    if (!token) return;
    try {
      const prompts = await analysisService.getDefaultPrompts(token);
      setDefaultPrompts(prompts);
      // Initialize overrides to defaults if empty
      setPromptOverridesState((prev) => Object.keys(prev).length ? prev : prompts);
    } catch (err) {
      // Soft-fail; dialog will handle errors
      console.error(err);
    }
  }, [token]);

  const setPromptOverride = useCallback((key: string, value: string) => {
    setPromptOverridesState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setPromptOverrides = useCallback((overrides: PromptOverrides) => {
    setPromptOverridesState(overrides || {});
  }, []);

  const clearPromptOverrides = useCallback(() => {
    setPromptOverridesState({});
  }, []);

  const value: AnalysisContextType = {
    tasks,
    selectedTasks,
    loading,
    error,
    loadTasks,
    toggleTask,
    selectAllTasks,
    clearTasks,
    defaultPrompts,
    promptOverrides,
    loadDefaultPrompts,
    setPromptOverride,
    setPromptOverrides,
    clearPromptOverrides,
  };

  return <AnalysisContext.Provider value={value}>{children}</AnalysisContext.Provider>;
};

